'use client';

import React, { createContext, useContext, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { userQueryOptions } from '@/app/providers/UserProvider';
import { LocalStorageKeys } from '@/app/lib/constants';

interface DailySubscriberContextValue {
  isSubscribed: boolean;
  markSubscribed: (email: string) => void;
}

const DailySubscriberContext = createContext<DailySubscriberContextValue>({
  isSubscribed: false,
  markSubscribed: () => {},
});

// localStorage-backed store for the subscriber email, so both the initial read and
// markSubscribed's write flow through useSyncExternalStore without a mount effect.
const emailKey = LocalStorageKeys.DAILY_REMINDER_SUBSCRIBER_EMAIL;
const emailListeners = new Set<() => void>();
function subscribeLocalEmail(cb: () => void) {
  emailListeners.add(cb);
  return () => emailListeners.delete(cb);
}
function hasLocalEmailSnapshot() {
  return localStorage.getItem(emailKey) !== null;
}
function writeLocalEmail(email: string) {
  localStorage.setItem(emailKey, email);
  emailListeners.forEach((cb) => cb());
}

export function DailySubscriberProvider({ children }: { children: React.ReactNode }) {
  // A signed-in player's membership arrives with /api/me; anyone else only has this browser's word.
  const onList = useQuery({ ...userQueryOptions, select: (data) => data.dailyReminders }).data ?? false;
  const hasLocalEmail = useSyncExternalStore(subscribeLocalEmail, hasLocalEmailSnapshot, () => false);

  return (
    <DailySubscriberContext.Provider value={{ isSubscribed: hasLocalEmail || onList, markSubscribed: writeLocalEmail }}>
      {children}
    </DailySubscriberContext.Provider>
  );
}

export function useDailySubscriberStatus(): DailySubscriberContextValue {
  return useContext(DailySubscriberContext);
}
